import { useEffect, useRef } from 'react'
import {
  useClearNotifications,
  useMarkNotificationsRead,
  useNotifications,
  type Notification,
} from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import { IconBell, IconBook, IconHeart, IconMessage, IconStar4, Skeleton, cn } from '@lb/ui'

import { http } from '../../lib/client'
import { formatRelativeTime } from '../../lib/format'

/**
 * `.tb-panel` — the bell's popover.
 *
 * ── Bell and megaphone are different things ───────────────────────────────
 * The handoff is explicit: the bell is activity about YOU — replies, reactions,
 * reminders — and the megaphone is official announcements. Different endpoints,
 * different shapes, different read semantics. `MemberAnnouncement` carries no
 * read state at all; `Notification` carries `readAt`.
 *
 * Conflating them is how somebody's reply notification ends up in a broadcast
 * list, and it is why this reads `/notifications` and not the ticker.
 */

/**
 * `.tb-notif-kind` — the glyph, keyed off the REAL event catalogue.
 *
 * ── Why this is a table and not a substring match ─────────────────────────
 * The first version matched `reply`/`reaction`/`announce`/`event`/`course`
 * against the key. The backend supplied the actual seven, and the guess misses
 * three of them — `content.digest`, `challenge.start` and, worst,
 * `safety.reachout`, which is the one row that must never fall through to a
 * default glyph. It is the only `MANDATORY` event in the catalogue: a member
 * cannot switch it off, because it fires when somebody has reached out about
 * their wellbeing.
 *
 * The keys are data — `GET /notifications/settings` returns every event with
 * the caller's own preference resolved, and a content manager can add one
 * without a deploy. So an unknown key is expected rather than exceptional, and
 * falls back to the bell instead of rendering nothing.
 */
const GLYPH: Record<string, typeof IconMessage> = {
  'post.reply': IconMessage,
  'post.reaction': IconHeart,
  'post.mention': IconMessage,
  'content.digest': IconBook,
  'announcement.broadcast': IconBell,
  'challenge.start': IconStar4,
  'safety.reachout': IconHeart,
}

function iconFor(eventKey: string): typeof IconMessage {
  return GLYPH[eventKey] ?? IconBell
}

/**
 * The title a row shows, when the API has none to give.
 *
 * ── The API returns the event key AS the title ────────────────────────────
 * A notification renders from a template written in the back office, and for
 * the seven product events no template is written yet in either locale — so
 * `/notifications` answers `title: 'announcement.broadcast'`, `body: ''`, and
 * the panel printed a machine key to a parent. That is data, not a client
 * bug, but the client can do better than echo it: the catalogue is known, so
 * each event gets a sentence here, and anything outside it a generic one.
 *
 * Matched on `title === eventKey` first — the honest signal that nothing was
 * written — and on the key's shape second, for a title that merely LOOKS like
 * a key. A real template is never `word.word`. Once a template is written the
 * title stops matching and the server's copy wins, unchanged.
 *
 * Nested by segment (`notifications.kind.post.reply`) because i18next reads
 * the dot as a path — a flat `'post.reply'` key would never resolve. Mirrored
 * in `laboussle-mobile/src/screens/notifications/index.tsx`.
 */
export function humaniseTitle(
  item: Pick<Notification, 'title' | 'eventKey'>,
  t: (key: string) => string,
  exists: (key: string) => boolean,
): string {
  const looksLikeKey = item.title === item.eventKey || /^[a-z]+\.[a-z]+$/.test(item.title)
  if (!looksLikeKey) return item.title
  const key = `notifications.kind.${item.eventKey}`
  return t(exists(key) ? key : 'notifications.kind.generic')
}

export function NotificationsPanel({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation()
  const feed = useNotifications(http)
  const markRead = useMarkNotificationsRead(http)
  const clearAll = useClearNotifications(http)
  const panel = useRef<HTMLDivElement>(null)

  /*
   * Dismiss on outside click and on Escape.
   *
   * Both, not one: a popover that only closes on click is a trap for anyone
   * driving from the keyboard, and this one is anchored to a control in the
   * topbar that is reachable by tab on every screen.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose()
    const onClick = (event: MouseEvent) => {
      if (panel.current && !panel.current.contains(event.target as Node)) onClose()
    }
    document.addEventListener('keydown', onKey)
    // Deferred: the click that OPENED the panel would otherwise close it again.
    const timer = setTimeout(() => document.addEventListener('click', onClick), 0)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('click', onClick)
      clearTimeout(timer)
    }
  }, [onClose])

  const items = feed.data?.items ?? []

  return (
    <div
      ref={panel}
      role="dialog"
      aria-label={t('notifications.title')}
      className={cn(
        'absolute end-0 top-[calc(100%+12px)] z-[160] w-[400px] max-w-[calc(100vw-32px)]',
        'overflow-hidden rounded-[var(--r-lg)] border border-border-deep bg-surface',
        'shadow-[0_28px_70px_-28px_rgb(31_45_58/0.45)]',
      )}
    >
      <header className="flex items-start justify-between gap-3.5 border-b border-border px-5 pb-3.5 pt-[18px]">
        <div>
          <h3 className="font-serif text-[20px] font-medium leading-[1.1] text-ink">
            {t('notifications.title')}
          </h3>
          <p className="mt-1 text-[12.5px] text-muted">
            {feed.data
              ? feed.data.unread > 0
                ? t('notifications.unreadCount', { count: feed.data.unread })
                : t('notifications.allRead')
              : ' '}
          </p>
        </div>
        {/*
         * `.tb-mark` — only when there is something to mark; and « Tout
         * effacer » whenever there is anything at all (client, 21 Sep). Clearing
         * empties the feed and the badge; nothing is deleted server-side.
         */}
        <div className="flex flex-none items-center gap-1">
          {feed.data && feed.data.unread > 0 ? (
            <button
              type="button"
              onClick={() => markRead.mutate([])}
              disabled={markRead.isPending}
              className="px-1.5 py-1 text-[12.5px] font-semibold text-ink hover:underline disabled:opacity-50"
            >
              {t('notifications.markAll')}
            </button>
          ) : null}
          {feed.data && feed.data.total > 0 ? (
            <button
              type="button"
              onClick={() => clearAll.mutate()}
              disabled={clearAll.isPending}
              className="px-1.5 py-1 text-[12.5px] font-semibold text-muted hover:text-ink hover:underline disabled:opacity-50"
            >
              {t('notifications.clearAll')}
            </button>
          ) : null}
        </div>
      </header>

      <div className="max-h-[420px] overflow-y-auto">
        {feed.isPending ? (
          <div className="flex flex-col gap-3 p-5">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : feed.isError ? (
          <p className="p-5 text-[13.5px] text-muted">{t('notifications.error')}</p>
        ) : items.length === 0 ? (
          <p className="p-5 text-[13.5px] text-muted">{t('notifications.empty')}</p>
        ) : (
          <ul className="flex flex-col">
            {items.map((item) => (
              <Row key={item.id} item={item} locale={i18n.language} onRead={() => markRead.mutate([item.id])} />
            ))}
          </ul>
        )}
      </div>

      {/*
       * `.tb-panel-foot` — says what this list is, because the bell and the
       * megaphone look alike in a topbar and only one of them is about you.
       */}
      <p className="border-t border-border bg-cream-dark px-5 py-3 text-[11.5px] leading-[1.45] text-muted">
        {t('notifications.foot')}
      </p>
    </div>
  )
}

function Row({
  item,
  locale,
  onRead,
}: {
  item: Notification
  locale: string
  onRead: () => void
}) {
  const { t, i18n } = useTranslation()
  const Glyph = iconFor(item.eventKey)
  const unread = item.readAt === null
  const title = humaniseTitle(item, t, i18n.exists.bind(i18n))

  return (
    <li>
      <button
        type="button"
        onClick={onRead}
        className={cn(
          'flex w-full items-start gap-3 border-b border-border px-5 py-[13px] text-left transition-colors last:border-b-0',
          unread ? 'bg-[rgb(232_197_181/0.14)] hover:bg-[rgb(232_197_181/0.24)]' : 'hover:bg-cream-dark',
        )}
      >
        <span className="relative flex-none">
          <span className="grid h-[34px] w-[34px] place-items-center rounded-full bg-bleu-gris-light text-ink-on-tint">
            <Glyph size={16} title={null} />
          </span>
          <span className="absolute -bottom-1 -end-1 grid h-[17px] w-[17px] place-items-center rounded-full border-2 border-surface bg-ink text-cream">
            <Glyph size={9} title={null} />
          </span>
        </span>

        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] leading-[1.45] text-ink">
            <strong className="font-semibold">{title}</strong>
            {item.body ? <> — {item.body}</> : null}
          </span>
          <span className="mt-[3px] block text-[11.5px] text-muted">
            {formatRelativeTime(item.createdAt, locale)}
          </span>
        </span>

        {/* `.tb-unread-dot` — the row's own state, beside the row. */}
        {unread ? <span aria-hidden className="mt-1.5 h-2 w-2 flex-none rounded-full bg-ink" /> : null}
      </button>
    </li>
  )
}
